import { Accessor, createMemo, Match, Switch, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { VideoData } from '../videos/videos-types';
import { BlogNewsData, BlogNewsListCardData } from '../blog-news/blog-news';
import { fetchProducts } from '../products/fetch-products';
import { fetchVideos } from '../videos/fetch-videos';
import { fetchBlogsAndNews } from '../blogs-news/fetch-blogs-and-news';
import { fetchProduct } from '../product/fetch-product';
import { fetchVideo } from '../video/fetch-video';
import { fetchBlogAndNews } from '../blog-news/fetch-blog-and-news';
import { ProductData, ProductListCardData } from '../product/product-types';
import { ErrorCatcher } from '../../tools/error-catcher';
import { SliderCard } from './slider-card/slider-card';
import { mapBlogNewsToSliderCard, mapProductToSliderCard, mapVideoToSliderCard } from './slider-card/map-post-to-slider-card';

type RequestParamsType = {
    tag?: {
        slug: string;
        name: string;
    };
    key?: string;
    limit?: number;
}

// export type PostTypeSlug = 'custom' | 'document' | 'page' | 'product' | 'video-item' | 'video-item-hcp' | 'blog-and-news-hcp' | 'blog-and-news';
export type PostTypeSlug = 'product' | 'video-item' | 'blog-and-news';

export type PostTypeType = {
    label: string;
    value: PostTypeSlug;
    tagSlug?: string;
    categorySlug?: string;
}

type CardTypeFactoryProps = {
    dynamicCards: boolean;
    tag?: {
        slug: string;
        name: string;
    };
    posttype: PostTypeType;
    postSlug?: string;
    showBorder?: boolean;
}

export const CardTypeFactory = (props: CardTypeFactoryProps) => {
    const { localize, videosEntityName } = useContext(AppContext);

    /* Every post type renders as the same SliderCard — a slider mixing types would
       otherwise put three different card designs next to each other. */
    const cardCreators = {
        'product': (product: ProductListCardData) => <SliderCard data={mapProductToSliderCard(product)} showBorder={props.showBorder} />,
        'video-item': (video: VideoData) => <SliderCard data={mapVideoToSliderCard(video, videosEntityName)} showBorder={props.showBorder} />,
        'blog-and-news': (post: BlogNewsListCardData) => (
            <SliderCard
                data={mapBlogNewsToSliderCard(post, post?.categories?.[0]?.name || localize('blog', 'Blog'))}
                showBorder={props.showBorder}
            />
        ),
    };

    const createCards = () => {
        const dataFetchers = {
            'product': (params: RequestParamsType) => fetchProducts(params),
            'video-item': (params: RequestParamsType) => fetchVideos(params),
            'blog-and-news': (params: RequestParamsType) => fetchBlogsAndNews(params),
        };

        const requestParams: RequestParamsType = {
            limit: 10,
        };

        if (props.tag?.slug) {
            requestParams.tag = props.tag;
        }

        const data: Accessor<ProductListCardData[]> | Accessor<VideoData[]> | Accessor<BlogNewsListCardData[]> = dataFetchers[props.posttype.value](requestParams);

        return data()?.map(item => {
            return cardCreators[props.posttype.value](item as any);
        });
    };

    const createSingleCard = () => {
        //TODO: these methods tend to lose reactivity, check if this still works.
        const dataFetchers = {
            'product': (slug: string) => createMemo(() => fetchProduct(slug)),
            'video-item': (slug: string) => createMemo(() => fetchVideo(slug)),
            'blog-and-news': (slug: string) => createMemo(() => fetchBlogAndNews(slug)),
        };

        if (!props.postSlug) {
            return;
        }

        const data: Accessor<ProductData> | Accessor<VideoData> | Accessor<BlogNewsData> = dataFetchers[props.posttype.value](props.postSlug);

        const post = data?.();

        /* A slug whose post has been deleted or hidden fetches nothing, and the mappers
           turn that into a well-formed empty card. Bail before one reaches the slider. */
        if (!post) {
            return null;
        }

        return cardCreators[props.posttype.value](post as any);
    };

    return (
        <ErrorCatcher componentName='Card factory'>
            <Switch>
                <Match when={props.dynamicCards}>
                    { createCards() }
                </Match>
                <Match when={!props.dynamicCards}>
                    { createSingleCard() }
                </Match>
            </Switch>
        </ErrorCatcher>
    );
};
